import { DxIdVersion, SupportedDxObjectType } from '@fullstory/proto/fs/api/proactiveinsights';
import {
  AlertConfigsPublic,
  AlertNotificationConfig,
  UserAlertPreference,
} from '@fullstory/proto/fs/services/dx-objects';
import { int64 } from '@fullstory/proto/google/protobuf';
import { RequestActionType, createGRPCAction } from '@fullstory/redux-util';
import jstz from 'jstz';
import { MetricAlertsRequestTypes } from './types';
import {
  invalidateMetricAlertCacheKeys,
  metricAlertCreateEditCacheKey,
  metricAlertDetailCacheKey,
  metricAlertListCacheKey,
} from './utils';

export const MetricAlertsRequestActions = {
  archiveAlertConfig: (orgId: string, metricId: string, alertConfigId: string) =>
    createGRPCAction<typeof AlertConfigsPublic.ArchiveAlertConfig>()(
      {
        type: MetricAlertsRequestTypes.ArchiveAlertConfigRequest,
        grpcFunction: AlertConfigsPublic.ArchiveAlertConfig,
        readWrite: 'write',
        body: { orgId, alertConfigId, archived: true },
        invalidateCacheKey: invalidateMetricAlertCacheKeys({ orgId, metricId, alertConfigId }),
      },
      MetricAlertsRequestTypes.ArchiveAlertConfigSuccess,
      MetricAlertsRequestTypes.ArchiveAlertConfigFailure,
    ),
  createAlertConfig: (orgId: string, alertNotificationConfig: AlertNotificationConfig) =>
    createGRPCAction<typeof AlertConfigsPublic.CreateAlertNotificationConfig>()(
      {
        type: MetricAlertsRequestTypes.CreateAlertConfigRequest,
        grpcFunction: AlertConfigsPublic.CreateAlertNotificationConfig,
        readWrite: 'write',
        body: { orgId, alertNotificationConfig },
        invalidateCacheKey: invalidateMetricAlertCacheKeys({
          orgId,
          metricId: alertNotificationConfig.alertConfig?.dxIdVersion?.id,
        }),
        // always make the request when creating
        force: true,
      },
      MetricAlertsRequestTypes.CreateAlertConfigSuccess,
      MetricAlertsRequestTypes.CreateAlertConfigFailure,
    ),
  duplicateAlertConfig: (orgId: string, metricId: string, alertConfigId: string) =>
    createGRPCAction<typeof AlertConfigsPublic.DuplicateAlert>()(
      {
        type: MetricAlertsRequestTypes.DuplicateAlertConfigRequest,
        grpcFunction: AlertConfigsPublic.DuplicateAlert,
        readWrite: 'write',
        body: { orgId, alertConfigId },
        invalidateCacheKey: invalidateMetricAlertCacheKeys({
          orgId,
          metricId,
          alertConfigId,
        }),
        // always make the request when duplicating
        force: true,
      },
      MetricAlertsRequestTypes.CreateAlertConfigSuccess,
      MetricAlertsRequestTypes.CreateAlertConfigFailure,
    ),
  getAlertsDetailView: (orgId: string, alertId: string) =>
    createGRPCAction<typeof AlertConfigsPublic.GetAlertsDetailView>({
      cacheKey: () => metricAlertDetailCacheKey({ orgId, alertId }),
    })(
      {
        type: MetricAlertsRequestTypes.GetAlertsDetailViewRequest,
        grpcFunction: AlertConfigsPublic.GetAlertsDetailView,
        readWrite: 'read',
        body: { orgId, alertId },
      },
      MetricAlertsRequestTypes.GetAlertsDetailViewSuccess,
      MetricAlertsRequestTypes.GetAlertsDetailViewFailure,
    ),
  getAlertsListView: (orgId: string, metricId?: string, archived: boolean = false) => {
    const dxIdVersion: DxIdVersion | null = metricId
      ? {
          id: metricId,
          version: int64.fromNumber(0), // this will be calculated on the backend,
          supportedDxObjectType: SupportedDxObjectType.SUPPORTED_DX_OBJECT_TYPE_METRIC,
        }
      : null;

    return createGRPCAction<typeof AlertConfigsPublic.GetAlertsListView>({
      cacheKey: () => metricAlertListCacheKey({ orgId, metricId, archived }),
    })(
      {
        type: MetricAlertsRequestTypes.GetAlertsListViewRequest,
        grpcFunction: AlertConfigsPublic.GetAlertsListView,
        readWrite: 'read',
        body: { orgId, dxIdVersion, archived },
      },
      MetricAlertsRequestTypes.GetAlertsListViewSuccess,
      MetricAlertsRequestTypes.GetAlertsListViewFailure,
    );
  },
  getCreateAlertView: (orgId: string, metricId: string, alertConfigId?: string) =>
    createGRPCAction<typeof AlertConfigsPublic.GetCreateAlertView>({
      cacheKey: () => metricAlertCreateEditCacheKey({ orgId, metricId, alertConfigId }),
    })(
      {
        type: MetricAlertsRequestTypes.GetCreateAlertViewRequest,
        grpcFunction: AlertConfigsPublic.GetCreateAlertView,
        readWrite: 'read',
        force: true,
        body: {
          orgId,
          metricId,
          alertConfigId: alertConfigId ?? '',
          timezone: jstz.determine().name(),
        },
        settings: {
          headers: {
            'GRPC-Timeout': '1M', // allow for 1 minute before context deadline exceeded
          },
        },
      },
      MetricAlertsRequestTypes.GetCreateAlertViewSuccess,
      MetricAlertsRequestTypes.GetCreateAlertViewFailure,
    ),
  unarchiveAlertConfig: (orgId: string, metricId: string, alertConfigId: string) =>
    createGRPCAction<typeof AlertConfigsPublic.ArchiveAlertConfig>()(
      {
        type: MetricAlertsRequestTypes.UnarchiveAlertConfigRequest,
        grpcFunction: AlertConfigsPublic.ArchiveAlertConfig,
        readWrite: 'write',
        body: { orgId, alertConfigId, archived: false },
        invalidateCacheKey: invalidateMetricAlertCacheKeys({ orgId, metricId, alertConfigId }),
      },
      MetricAlertsRequestTypes.UnarchiveAlertConfigSuccess,
      MetricAlertsRequestTypes.UnarchiveAlertConfigFailure,
    ),
  updateAlertConfig: (
    orgId: string,
    alertConfigId: string,
    alertNotificationConfig: AlertNotificationConfig,
    metricId?: string,
  ) =>
    createGRPCAction<typeof AlertConfigsPublic.UpdateAlertNotificationConfig>()(
      {
        type: MetricAlertsRequestTypes.UpdateAlertNotificationConfigRequest,
        grpcFunction: AlertConfigsPublic.UpdateAlertNotificationConfig,
        readWrite: 'write',
        force: true,
        body: {
          orgId,
          alertConfigId,
          alertConfig: alertNotificationConfig.alertConfig,
          addDestinationIds: alertNotificationConfig.destinationIds,
          removeDestinationIds: [],
          updatedSubscriptions: alertNotificationConfig.userAlertPreferences,
        },
        invalidateCacheKey: invalidateMetricAlertCacheKeys({ orgId, metricId, alertConfigId }),
      },
      MetricAlertsRequestTypes.UpdateAlertNotificationConfigSuccess,
      MetricAlertsRequestTypes.UpdateAlertNotificationConfigFailure,
    ),
  subscribeToAlertConfig: (
    orgId: string,
    alertConfigId: string,
    userAlertPreference?: UserAlertPreference,
  ) =>
    createGRPCAction<typeof AlertConfigsPublic.SubscribeAlertNotificationConfig>()(
      {
        type: MetricAlertsRequestTypes.SubscribeAlertNotificationConfigRequest,
        grpcFunction: AlertConfigsPublic.SubscribeAlertNotificationConfig,
        readWrite: 'write',
        force: true,
        body: {
          orgId,
          alertConfigId,
          updatedSubscription: userAlertPreference ?? null,
        },
        invalidateCacheKey: invalidateMetricAlertCacheKeys({ orgId, alertConfigId }),
      },
      MetricAlertsRequestTypes.UpdateAlertNotificationConfigSuccess,
      MetricAlertsRequestTypes.UpdateAlertNotificationConfigFailure,
    ),
  updateAlertsForMetric: (orgId: string, metricId: string, alertIds?: string[]) => {
    const dxIdVersion: DxIdVersion = {
      id: metricId,
      version: int64.fromNumber(0), // this will be calculated on the backend,
      supportedDxObjectType: SupportedDxObjectType.SUPPORTED_DX_OBJECT_TYPE_METRIC,
    };

    const metricAlertCacheKeysToInvalidate =
      alertIds?.flatMap(alertConfigId =>
        invalidateMetricAlertCacheKeys({ orgId, metricId, alertConfigId }),
      ) ?? [];

    return createGRPCAction<typeof AlertConfigsPublic.UpdateAlertsForMetric>()(
      {
        type: MetricAlertsRequestTypes.UpdateAlertsForMetricRequest,
        grpcFunction: AlertConfigsPublic.UpdateAlertsForMetric,
        readWrite: 'write',
        body: {
          orgId,
          dxIdVersion,
        },
        invalidateCacheKey: metricAlertCacheKeysToInvalidate,
        force: true,
      },
      MetricAlertsRequestTypes.UpdateAlertsForMetricSuccess,
      MetricAlertsRequestTypes.UpdateAlertsForMetricFailure,
    );
  },
};

export type MetricAlertsRequestActions = RequestActionType<typeof MetricAlertsRequestActions>;
